import React from 'react';
import {
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { TValidationType } from '../../types/validation-types';
import { LinkTableStyled } from './StyledComponents';

export interface ValidationErrorRow {
  entityId: string; // extractionId or documentId
  field: string;
  type: TValidationType;
  error: string | null;
  errorDetail: string | null;
  dataset?: string;
  dismissalReason?: string | null;
}

interface ValidationErrorsTableProps {
  errors: ValidationErrorRow[];
  entityIdLabel?: string; // "Extraction ID" or "Document ID"
  showDatasetColumn?: boolean;
  showDismissalColumn?: boolean;
  getEntityLink?: (entityId: string) => string; // Optional function to generate link for entity
}

const ValidationErrorsTable: React.FC<ValidationErrorsTableProps> = ({
  errors,
  entityIdLabel = 'ID',
  showDatasetColumn = false,
  showDismissalColumn = false,
  getEntityLink,
}) => {
  if (errors.length === 0) {
    return (
      <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
        No validation errors found.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ maxHeight: '50vh' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell width="18%" align="left" sx={{ whiteSpace: 'nowrap' }}>
              {entityIdLabel}
            </TableCell>
            <TableCell width="15%" align="left">
              Field
            </TableCell>
            <TableCell width="10%" align="center">
              Type
            </TableCell>
            <TableCell width={showDatasetColumn || showDismissalColumn ? '27%' : '57%'} align="left">
              Error Details
            </TableCell>
            {showDatasetColumn && (
              <TableCell width="10%" align="left">
                Dataset
              </TableCell>
            )}
            {showDismissalColumn && (
              <TableCell width="20%" align="left" sx={{ whiteSpace: 'nowrap' }}>
                Dismissal Reason
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {errors.map((error, index) => (
            <TableRow key={index} data-cy={`validation-error-row-${index}`}>
              <TableCell align="left">
                {getEntityLink ? (
                  <LinkTableStyled to={getEntityLink(error.entityId)}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'inherit' }}>
                      {error.entityId}
                    </Typography>
                  </LinkTableStyled>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 'medium', color: 'primary.main' }}
                  >
                    {error.entityId}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="left">
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {error.field}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Chip label={error.type} size="small" color="warning" variant="outlined" />
              </TableCell>
              <TableCell align="left">
                <Box>
                  {error.error && (
                    <Typography variant="body2" color="error" sx={{ mb: 0.5 }}>
                      {error.error}
                    </Typography>
                  )}
                  {error.errorDetail && (
                    <Typography variant="body2" color="text.secondary">
                      {error.errorDetail}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              {showDatasetColumn && (
                <TableCell align="left">
                  <Typography variant="body2" color="text.secondary">
                    {error.dataset || 'N/A'}
                  </Typography>
                </TableCell>
              )}
              {showDismissalColumn && (
                <TableCell align="left">
                  {error.dismissalReason ? (
                    <Chip
                      label={error.dismissalReason}
                      size="small"
                      color="default"
                      variant="filled"
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ValidationErrorsTable;
